'use client';

import { useState } from 'react';

import Link from 'next/link';

import {
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  type LucideIcon,
  Network,
  Package,
  Plane,
  ReceiptText,
  RotateCcw,
  Ship,
  TrendingUp,
} from 'lucide-react';

import { DEFAULT_CURRENCY, formatMoney } from '@kit/shared/currency';
import { formatQuantity } from '@kit/shared/quantity';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Input } from '@kit/ui/input';
import { Label } from '@kit/ui/label';
import { cn } from '@kit/ui/utils';

import appConfig from '~/config/app.config';
import pathsConfig from '~/config/paths.config';

import { CurrencyPicker } from '../_components/currency-picker';
import { RelatedTools } from '../_components/related-tools';
import {
  FREIGHT_ASSUMPTIONS,
  calculateFreightCost,
  getFreightInputError,
  parseNumericInput,
} from './_lib/calculate-freight-cost';

const baseUrl = appConfig.url;
const siteName = appConfig.name;
const calculatorUrl = `${baseUrl}/tools/freight-cost-estimator`;
const toolsUrl = `${baseUrl}/tools`;

type FormData = {
  airFixedCharges: string;
  airRatePerKg: string;
  airTransitDays: string;
  cargoValue: string;
  seaFixedCharges: string;
  seaMinimumRevenueTons: string;
  seaRatePerRevenueTon: string;
  seaTransitDays: string;
  volume: string;
  weight: string;
};

const DEFAULT_FORM_DATA: FormData = {
  airFixedCharges: '85',
  airRatePerKg: '4.80',
  airTransitDays: '7',
  cargoValue: '40000',
  seaFixedCharges: '285',
  seaMinimumRevenueTons: '1',
  seaRatePerRevenueTon: '62',
  seaTransitDays: '15',
  volume: '2.5',
  weight: '500',
};

const heroHighlights = [
  'Uses your quote rates, not a fake market price',
  'Correct W/M and volumetric math',
  'No signup required',
] as const;

const estimationSteps = [
  {
    title: '1. Chargeable weight vs volume',
    description:
      'Sea freight bills the greater of volume and weight/measure at 1 CBM per 1,000 kg. Air freight bills the greater of gross weight and volumetric weight at 167 kg per CBM.',
    icon: Package,
  },
  {
    title: '2. Lane rates and fixed charges',
    description:
      'Enter the variable rates and fixed charges from your forwarder quotes. This avoids pretending that a static website rate is a live, bookable price.',
    icon: Ship,
  },
  {
    title: '3. The cost of waiting',
    description:
      'The cheaper mode is not always the better one. Enter a cargo value and the calculator prices the carrying cost that faster transit releases.',
    icon: Clock,
  },
] as const;

const whyItMatters = [
  {
    title: 'Choose the right shipping mode',
    description:
      'A sea vs air freight comparison keeps you from paying for speed when the business case is not there.',
  },
  {
    title: 'Protect gross margin',
    description:
      'Freight swings can materially change unit economics. Estimating freight cost early keeps margin assumptions grounded.',
  },
  {
    title: 'Set better delivery expectations',
    description:
      'Transit-time benchmarks let ops, purchasing, and finance work from the same timeline before booking cargo or requesting a freight quote.',
  },
] as const;

const faqItems = [
  {
    value: 'free',
    question: 'Is this sea vs air freight calculator free?',
    answer:
      'Yes. This free sea vs air freight calculator runs entirely in your browser with no signup, no usage limit, and no credit card. Nothing you enter is sent to a server, and results update as you type.',
  },
  {
    value: 'sea-vs-air-cost',
    question: 'How much more expensive is air freight than sea freight?',
    answer:
      'It depends on the current lane, shipment density, season, and included charges. Enter the per-unit rates and fixed charges from comparable forwarder quotes to calculate the difference for your shipment.',
  },
  {
    value: 'sea-cost',
    question: 'How does the sea freight side estimate cost?',
    answer:
      'It models LCL weight-or-measure pricing. The billable revenue tons are the greater of cubic metres, metric tons, and the minimum from your quote, multiplied by the rate you enter. The example starts with a one W/M minimum. It then adds your quote’s fixed charges.',
  },
  {
    value: 'air-weight',
    question: 'How does the air freight side estimate chargeable weight?',
    answer:
      'Air freight bills the greater of gross weight and volumetric weight. Volumetric weight uses the IATA 1:6000 divisor, which works out to 167 kg per cubic metre. Enter handling charges separately, and use the carrier’s final quoted total when a minimum freight charge applies.',
  },
  {
    value: 'quote-scope',
    question: 'Which freight charges should I enter?',
    answer:
      'Use comparable quote scopes. Fixed charges can include pickup, documentation, terminal handling, fuel, security, customs clearance, and final delivery when they are not already in the variable rate. Keep insurance, duty, and tax consistent across both options.',
  },
  {
    value: 'mode-choice',
    question: 'When does air freight make sense?',
    answer:
      'Air is usually justified when the delivery date protects sales, launch timing, or working capital. Enter a cargo value and the calculator shows the break-even point where the carrying cost released by faster transit offsets the air premium.',
  },
  {
    value: 'worked-example',
    question: 'What does a typical sea vs air comparison look like?',
    answer:
      'The page opens with a 500 kg, 2.5 CBM Shanghai to Los Angeles LCL example: sea at $62 per W/M plus $285 fixed ($440 total, 15 days) versus air at $4.80/kg plus $85 ($2,485 total, 7 days). That is a $2,045 air premium for about 8 days. At a $40,000 cargo value and 20% carrying cost, faster transit only releases about $175 — so sea wins unless the delivery date protects revenue. Replace every rate with your own quotes.',
  },
  {
    value: 'accuracy',
    question: 'Are these live freight market quotes?',
    answer:
      'No. Freight prices and surcharges change too quickly for a static calculator to promise a bookable rate. This tool applies published chargeable-weight rules to the rates you enter. Transit windows are planning guides and final pricing must be confirmed with a carrier or forwarder.',
  },
] as const;

function StructuredData() {
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Freight Cost Calculator: Sea vs Air From Your Quote',
    alternateName: 'Freight Cost Estimator',
    description:
      'Compare sea and air freight from your forwarder quotes: chargeable weight, totals, transit time, and the air premium.',
    url: calculatorUrl,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Freight Rate Calculator',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    inLanguage: 'en',
    isAccessibleForFree: true,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    creator: {
      '@type': 'Organization',
      name: siteName,
      url: baseUrl,
    },
    featureList: [
      'Compare sea freight vs air freight cost',
      'Model chargeable weight and revenue tons',
      'Estimate transit time ranges by lane',
      'Use rates and fixed charges from forwarder quotes',
      'Value the carrying cost of slower transit',
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Free Tools',
        item: toolsUrl,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Sea vs Air Freight Calculator',
        item: calculatorUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webAppSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
    </>
  );
}

function formatCurrency(value: number | null | undefined, currency: string) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '--';
  }

  return formatMoney({ currency, value });
}

function formatTransit(range: { max: number; min: number }) {
  if (range.min === range.max) {
    return `${range.min} days`;
  }

  return `${range.min}-${range.max} days`;
}

/**
 * One panel per step of the form. The fields used to run as a single flat
 * two-column grid, which put a sea field beside an air field on every row and
 * made ten inputs read as one undifferentiated wall.
 */
function FormSection({
  children,
  eyebrow,
  footer,
  icon: Icon,
  subtitle,
  title,
}: {
  children: React.ReactNode;
  eyebrow: string;
  footer?: React.ReactNode;
  icon: LucideIcon;
  subtitle: string;
  title: string;
}) {
  return (
    <section className="border-border bg-muted flex flex-col gap-5 rounded-2xl border p-5 sm:p-6">
      <div className="flex items-start gap-3">
        <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
          <Icon className="h-5 w-5" />
        </span>

        <div className="min-w-0">
          <p className="text-muted-foreground text-[11px] font-bold tracking-[0.18em] uppercase">
            {eyebrow}
          </p>
          <h3 className="text-foreground text-base font-bold">{title}</h3>
          <p className="text-muted-foreground mt-0.5 text-sm leading-6">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="space-y-4">{children}</div>

      {/* mt-auto so the two quote panels finish on the same line even when one
          of them carries an extra field. */}
      {footer ? <div className="mt-auto">{footer}</div> : null}
    </section>
  );
}

/**
 * A labelled number input with its unit attached, rather than folded into the
 * label as "Sea rate (USD per W/M)". The unit belongs next to the number the
 * user is typing, and the currency should not have to be repeated in six
 * labels.
 */
function NumberField({
  hint,
  id,
  label,
  onChange,
  optional,
  placeholder,
  prefix,
  step,
  suffix,
  value,
}: {
  hint?: string;
  id: string;
  label: string;
  onChange: (value: string) => void;
  optional?: boolean;
  placeholder?: string;
  prefix?: string;
  step: string;
  suffix?: string;
  value: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-foreground text-sm font-semibold">
        {label}
        {optional ? (
          <span className="text-muted-foreground font-normal"> (optional)</span>
        ) : null}
      </Label>

      <div className="relative">
        {prefix ? (
          <span
            aria-hidden
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold"
          >
            {prefix}
          </span>
        ) : null}

        <Input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          step={step}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          // Spinners are useless on a freight rate and they collide with the
          // unit sitting in the same corner.
          className={cn(
            'border-border bg-card h-12 rounded-xl shadow-none',
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            prefix && 'pl-14',
            suffix && 'pr-20',
          )}
        />

        {suffix ? (
          <span
            aria-hidden
            className="text-muted-foreground pointer-events-none absolute top-1/2 right-3.5 -translate-y-1/2 text-sm"
          >
            {suffix}
          </span>
        ) : null}
      </div>

      {hint ? (
        <p className="text-muted-foreground text-xs leading-5">{hint}</p>
      ) : null}
    </div>
  );
}

/** Folds a field most people should leave alone out of the first read. */
function AdvancedField({
  children,
  summary,
}: {
  children: React.ReactNode;
  summary: string;
}) {
  return (
    <details className="group border-border rounded-xl border border-dashed px-3.5 py-2.5">
      <summary className="text-muted-foreground hover:text-foreground flex cursor-pointer list-none items-center gap-2 text-sm font-semibold [&::-webkit-details-marker]:hidden">
        <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
        {summary}
      </summary>

      <div className="pt-3">{children}</div>
    </details>
  );
}

/** The running total for one mode, so each panel shows its own effect. */
function SectionTotal({
  label,
  meta,
  value,
}: {
  label: string;
  meta?: string;
  value: string;
}) {
  return (
    <div className="border-border flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-t pt-4">
      <span className="text-muted-foreground text-sm font-semibold">
        {label}
      </span>

      <span className="text-foreground text-xl font-extrabold tabular-nums">
        {value}
      </span>

      {meta ? (
        <span className="text-muted-foreground w-full text-xs">{meta}</span>
      ) : null}
    </div>
  );
}

export default function FreightCostEstimatorPage() {
  const [formData, setFormData] = useState<FormData>(DEFAULT_FORM_DATA);
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);

  const startFreePath = pathsConfig.auth.startFree;
  const weightKg = parseNumericInput(formData.weight);
  const volumeCbm = parseNumericInput(formData.volume);
  const inputError = getFreightInputError({ volumeCbm, weightKg });
  const results = inputError
    ? null
    : calculateFreightCost({
        airFixedCharges: parseNumericInput(formData.airFixedCharges),
        currency,
        airRatePerKg: parseNumericInput(formData.airRatePerKg),
        airTransitDays: parseNumericInput(formData.airTransitDays),
        cargoValue: formData.cargoValue
          ? parseNumericInput(formData.cargoValue)
          : null,
        seaFixedCharges: parseNumericInput(formData.seaFixedCharges),
        seaMinimumRevenueTons: parseNumericInput(
          formData.seaMinimumRevenueTons,
        ),
        seaRatePerRevenueTon: parseNumericInput(formData.seaRatePerRevenueTon),
        seaTransitDays: parseNumericInput(formData.seaTransitDays),
        volumeCbm,
        weightKg,
      });
  const hasResults = results !== null;

  function updateField<K extends keyof FormData>(key: K, value: FormData[K]) {
    setFormData((current) => ({
      ...current,
      [key]: value,
    }));
  }

  function resetExample() {
    setFormData(DEFAULT_FORM_DATA);
  }

  return (
    <>
      <StructuredData />

      <div className="bg-background text-foreground min-h-screen">
        <section className="border-border bg-muted relative overflow-hidden border-b">
          <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top_right,_color-mix(in_srgb,var(--primary)_calc(0.12*100%),transparent),_transparent_42%)]" />
          <div className="bg-primary/10 absolute -top-28 right-0 h-72 w-72 rounded-full blur-3xl" />

          <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="grid items-start gap-12 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="flex flex-col gap-6 pt-2 lg:pt-10">
                {/*
                 * One name for one tool. This page called itself the Freight
                 * Cost Estimator in its own eyebrow and the Sea vs Air Freight
                 * Calculator in its own H1, while the nav said the first and
                 * the tools grid said the second, so the same link led to what
                 * read as two different products. The name is what the URL
                 * says; "sea vs air" is what it does, and stays in the eyebrow,
                 * the subtitle and the page title where search looks for it.
                 */}
                <div className="bg-primary/10 text-primary inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase">
                  <Ship className="h-4 w-4" />
                  Compare sea vs air
                </div>

                <div className="space-y-5">
                  <h1 className="font-heading text-foreground max-w-2xl text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                    <span className="text-primary">
                      Freight Cost Calculator
                    </span>
                    : Sea vs Air From Your Quote
                  </h1>

                  <p className="text-muted-foreground max-w-xl text-lg leading-8">
                    Free tool for FBA, Shopify, and importers. Paste two
                    forwarder quotes to compare LCL sea vs air totals,
                    chargeable weight, and whether the air premium is worth the
                    days saved.
                  </p>
                </div>

                <div className="flex flex-wrap gap-5 pt-2">
                  {heroHighlights.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-5 w-5" />
                      <span className="text-muted-foreground text-sm font-medium">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-muted-foreground text-sm leading-6">
                  The calculator opens with an illustrative Shanghai to Los
                  Angeles LCL shipment. Replace every rate with the matching
                  line from your own forwarder quotes.
                </p>
              </div>

              <div className="bg-foreground text-background rounded-[10px] p-7 shadow-[0_24px_70px_-30px_rgba(38,31,28,0.7)] sm:p-9">
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <p className="text-background/70 text-xs font-bold tracking-[0.18em] uppercase">
                      Lowest estimated freight cost
                    </p>
                    <p className="mt-3 text-5xl font-extrabold tracking-tight tabular-nums">
                      {hasResults
                        ? formatCurrency(
                            Math.min(
                              results.sea.totalCost,
                              results.air.totalCost,
                            ),
                            currency,
                          )
                        : '--'}
                    </p>
                    <p className="mt-2 text-sm font-semibold capitalize">
                      {hasResults
                        ? `${results.cheaperMode} freight`
                        : 'Add shipment details'}
                    </p>
                  </div>
                  <div className="bg-background/10 rounded-2xl p-3">
                    <Ship className="h-7 w-7" />
                  </div>
                </div>

                <div className="border-background/20 mt-8 grid grid-cols-2 gap-4 border-t pt-7">
                  {[
                    [
                      'Sea total',
                      hasResults
                        ? formatCurrency(results.sea.totalCost, currency)
                        : '--',
                    ],
                    [
                      'Air total',
                      hasResults
                        ? formatCurrency(results.air.totalCost, currency)
                        : '--',
                    ],
                    [
                      'LCL billable',
                      hasResults ? `${results.sea.revenueTons} W/M` : '--',
                    ],
                    [
                      'Air billable',
                      hasResults
                        ? `${formatQuantity(results.air.chargeableKg)} kg`
                        : '--',
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="bg-background/10 rounded-xl p-4"
                    >
                      <p className="text-background/70 text-xs">{label}</p>
                      <p className="mt-1 text-lg font-bold tabular-nums">
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center justify-between gap-4 text-sm">
                  <span className="text-background/70">Air premium</span>
                  <span className="font-bold tabular-nums">
                    {hasResults
                      ? formatCurrency(results.airPremium, currency)
                      : '--'}
                  </span>
                </div>
              </div>

              <div className="border-border bg-card rounded-[10px] border p-6 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)] sm:p-8 lg:col-span-2 lg:p-10">
                <div className="mb-6 flex items-center gap-3">
                  <div className="bg-primary/10 flex h-11 w-11 items-center justify-center rounded-xl">
                    <Ship className="text-primary h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-foreground text-xl font-bold">
                      Compare Sea vs Air Freight Costs
                    </h2>
                    <p className="text-muted-foreground text-sm">
                      Shipment details and rates from your quotes
                    </p>
                  </div>
                </div>

                <form
                  onSubmit={(e) => e.preventDefault()}
                  className="space-y-5"
                >
                  <FormSection
                    eyebrow="Step 1"
                    icon={Package}
                    subtitle="One shipment, priced two ways. Take these off the packing list or the supplier's proforma."
                    title="What you are shipping"
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <NumberField
                        id="weight"
                        label="Gross weight"
                        onChange={(next) => updateField('weight', next)}
                        placeholder="500"
                        step="0.1"
                        suffix="kg"
                        value={formData.weight}
                      />

                      <NumberField
                        id="volume"
                        label="Volume"
                        onChange={(next) => updateField('volume', next)}
                        placeholder="2.5"
                        step="0.01"
                        suffix="CBM"
                        value={formData.volume}
                      />
                    </div>

                    <CurrencyPicker
                      hint="Every rate and charge below is read in this currency. Nothing is converted."
                      onChange={setCurrency}
                      selectClassName="border-border bg-card focus-visible:ring-primary/20 h-12 rounded-xl shadow-none focus-visible:ring-2"
                      value={currency}
                    />
                  </FormSection>

                  {/* One panel per mode. Every figure in the sea panel comes
                      off the ocean quote and every figure in the air panel off
                      the air quote, so the form matches the two documents the
                      user is reading from. */}
                  <div className="grid gap-4 lg:grid-cols-2">
                    <FormSection
                      eyebrow="Step 2, from your ocean quote"
                      footer={
                        <SectionTotal
                          label="Sea total"
                          meta={
                            hasResults
                              ? `Billed on ${results.sea.revenueTons} W/M`
                              : undefined
                          }
                          value={
                            hasResults
                              ? formatCurrency(results.sea.totalCost, currency)
                              : '--'
                          }
                        />
                      }
                      icon={Ship}
                      subtitle="LCL is billed per W/M, whichever is greater: your cubic metres or your tonnes."
                      title="Sea freight"
                    >
                      <NumberField
                        id="seaRate"
                        label="Rate"
                        onChange={(next) =>
                          updateField('seaRatePerRevenueTon', next)
                        }
                        prefix={currency}
                        step="0.01"
                        suffix="per W/M"
                        value={formData.seaRatePerRevenueTon}
                      />

                      <NumberField
                        hint="The flat lines on the quote: terminal handling, documentation, customs clearance."
                        id="seaFixed"
                        label="Fixed charges"
                        onChange={(next) =>
                          updateField('seaFixedCharges', next)
                        }
                        prefix={currency}
                        step="0.01"
                        value={formData.seaFixedCharges}
                      />

                      <NumberField
                        id="seaTransit"
                        label="Transit time"
                        onChange={(next) => updateField('seaTransitDays', next)}
                        step="1"
                        suffix="days"
                        value={formData.seaTransitDays}
                      />

                      <AdvancedField summary="Set the LCL minimum">
                        <NumberField
                          hint="Most LCL quotes bill at least 1 W/M however small the shipment. Change it only if yours says otherwise."
                          id="seaMinimum"
                          label="Minimum billed"
                          onChange={(next) =>
                            updateField('seaMinimumRevenueTons', next)
                          }
                          step="0.1"
                          suffix="W/M"
                          value={formData.seaMinimumRevenueTons}
                        />
                      </AdvancedField>
                    </FormSection>

                    <FormSection
                      eyebrow="Step 2, from your air quote"
                      footer={
                        <SectionTotal
                          label="Air total"
                          meta={
                            hasResults
                              ? `Billed on ${results.air.chargeableKg.toLocaleString('en-US')} chargeable kg`
                              : undefined
                          }
                          value={
                            hasResults
                              ? formatCurrency(results.air.totalCost, currency)
                              : '--'
                          }
                        />
                      }
                      icon={Plane}
                      subtitle="Air is billed per chargeable kg, whichever is greater: your gross weight or your volumetric weight."
                      title="Air freight"
                    >
                      <NumberField
                        id="airRate"
                        label="Rate"
                        onChange={(next) => updateField('airRatePerKg', next)}
                        prefix={currency}
                        step="0.01"
                        suffix="per kg"
                        value={formData.airRatePerKg}
                      />

                      <NumberField
                        hint="The flat lines on the quote: air waybill, security screening, handling."
                        id="airFixed"
                        label="Fixed charges"
                        onChange={(next) =>
                          updateField('airFixedCharges', next)
                        }
                        prefix={currency}
                        step="0.01"
                        value={formData.airFixedCharges}
                      />

                      <NumberField
                        id="airTransit"
                        label="Transit time"
                        onChange={(next) => updateField('airTransitDays', next)}
                        step="1"
                        suffix="days"
                        value={formData.airTransitDays}
                      />
                    </FormSection>
                  </div>

                  <FormSection
                    eyebrow="Step 3, optional"
                    icon={TrendingUp}
                    subtitle="Skip it and you still get both totals. Add it and we can say whether the days air saves are worth what air costs."
                    title="What the cargo is worth"
                  >
                    <NumberField
                      hint="Money sitting on a ship is money you cannot spend. We price that at 20% a year."
                      id="cargoValue"
                      label="Cargo value"
                      onChange={(next) => updateField('cargoValue', next)}
                      optional
                      prefix={currency}
                      step="1"
                      value={formData.cargoValue}
                    />
                  </FormSection>

                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={resetExample}
                      className="h-12 w-full rounded-xl font-bold sm:w-auto"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" /> Reset example
                    </Button>
                  </div>
                  {inputError ? (
                    <div
                      aria-live="polite"
                      className="border-destructive/40 bg-destructive/10 text-destructive flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm"
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>{inputError}</p>
                    </div>
                  ) : null}

                  <p className="text-muted-foreground text-xs leading-5">
                    Results update instantly. The example rates are
                    illustrative, so replace them with comparable forwarder
                    quotes before making a booking decision.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="border-border bg-card border-y py-12">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="border-primary/10 bg-primary/[0.06] rounded-[10px] border p-6 sm:p-8">
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-bold tracking-[0.22em] uppercase">
                  Sea vs Air Comparison
                </p>
                <h2 className="font-heading text-foreground text-2xl font-bold">
                  Your comparable quote analysis
                </h2>
              </div>

              <div className="mt-8">
                <div className="space-y-6">
                  <div className="border-border bg-card/70 overflow-x-auto rounded-2xl border">
                    <table className="w-full min-w-[520px] text-left text-sm">
                      <thead>
                        <tr className="border-border text-muted-foreground border-b text-xs font-bold tracking-[0.14em] uppercase">
                          <th className="px-5 py-4">Line item</th>
                          <th className="px-5 py-4">
                            <span className="inline-flex items-center gap-2">
                              <Ship className="h-4 w-4" />
                              Sea (LCL)
                            </span>
                          </th>
                          <th className="px-5 py-4">
                            <span className="inline-flex items-center gap-2">
                              <Plane className="h-4 w-4" />
                              Air
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-foreground">
                        <tr className="border-border/60 border-b">
                          <td className="text-muted-foreground px-5 py-3">
                            Chargeable basis
                          </td>
                          <td className="px-5 py-3 font-semibold">
                            {hasResults
                              ? `${results.sea.revenueTons} R/T`
                              : '--'}
                          </td>
                          <td className="px-5 py-3 font-semibold">
                            {hasResults
                              ? `${results.air.chargeableKg.toLocaleString('en-US')} kg`
                              : '--'}
                          </td>
                        </tr>
                        <tr className="border-border/60 border-b">
                          <td className="text-muted-foreground px-5 py-3">
                            Variable freight
                          </td>
                          <td className="px-5 py-3">
                            {hasResults
                              ? formatCurrency(
                                  results.sea.mainFreight,
                                  currency,
                                )
                              : '--'}
                          </td>
                          <td className="px-5 py-3">
                            {hasResults
                              ? formatCurrency(
                                  results.air.mainFreight,
                                  currency,
                                )
                              : '--'}
                          </td>
                        </tr>
                        <tr className="border-border/60 border-b">
                          <td className="text-muted-foreground px-5 py-3">
                            Fixed charges entered
                          </td>
                          <td className="px-5 py-3">
                            {hasResults
                              ? formatCurrency(
                                  results.sea.fixedCharges,
                                  currency,
                                )
                              : '--'}
                          </td>
                          <td className="px-5 py-3">
                            {hasResults
                              ? formatCurrency(
                                  results.air.fixedCharges,
                                  currency,
                                )
                              : '--'}
                          </td>
                        </tr>
                        <tr className="border-border/60 border-b">
                          <td className="text-foreground px-5 py-4 font-bold">
                            Total estimated cost
                          </td>
                          <td className="text-primary px-5 py-4 text-xl font-black">
                            {hasResults
                              ? formatCurrency(results.sea.totalCost, currency)
                              : '--'}
                          </td>
                          <td className="text-foreground px-5 py-4 text-xl font-black">
                            {hasResults
                              ? formatCurrency(results.air.totalCost, currency)
                              : '--'}
                          </td>
                        </tr>
                        <tr className="border-border/60 border-b">
                          <td className="text-muted-foreground px-5 py-3">
                            Transit time
                          </td>
                          <td className="px-5 py-3 font-semibold">
                            {hasResults
                              ? formatTransit(results.sea.transitDays)
                              : '--'}
                          </td>
                          <td className="px-5 py-3 font-semibold">
                            {hasResults
                              ? formatTransit(results.air.transitDays)
                              : '--'}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted-foreground px-5 py-3">
                            Cost per actual kg
                          </td>
                          <td className="px-5 py-3">
                            {hasResults
                              ? formatCurrency(
                                  results.sea.costPerActualKg,
                                  currency,
                                )
                              : '--'}
                          </td>
                          <td className="px-5 py-3">
                            {hasResults
                              ? formatCurrency(
                                  results.air.costPerActualKg,
                                  currency,
                                )
                              : '--'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="border-border bg-card/70 rounded-2xl border p-4">
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                        Air Premium
                      </p>
                      <p className="text-foreground mt-2 text-lg font-bold">
                        {hasResults
                          ? formatCurrency(results.airPremium, currency)
                          : '--'}
                      </p>
                      <p className="text-muted-foreground mt-2 text-xs leading-5">
                        {hasResults
                          ? `For about ${results.daysSaved} days saved.`
                          : 'What speed costs on this lane.'}
                      </p>
                    </div>

                    <div className="border-border bg-card/70 rounded-2xl border p-4">
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                        Carrying Cost Released
                      </p>
                      <p className="text-foreground mt-2 text-lg font-bold">
                        {hasResults && results.carryingCostSaved !== null
                          ? formatCurrency(results.carryingCostSaved, currency)
                          : '--'}
                      </p>
                      <p className="text-muted-foreground mt-2 text-xs leading-5">
                        {!hasResults
                          ? 'Add quote inputs to compare working capital.'
                          : results.daysSaved <= 0
                            ? 'No carrying cost is released because air is not faster.'
                            : results.carryingCostSaved === null
                              ? 'Add a cargo value to price the faster cash cycle.'
                              : `Capital freed by arriving earlier, at ${Math.round(FREIGHT_ASSUMPTIONS.annualCarryingCostRate * 100)}% carrying cost.`}
                      </p>
                    </div>

                    <div className="border-border bg-card/70 rounded-2xl border p-4">
                      <p className="text-muted-foreground text-xs font-semibold tracking-[0.16em] uppercase">
                        Break-Even Cargo Value
                      </p>
                      <p className="text-foreground mt-2 text-lg font-bold">
                        {hasResults
                          ? formatCurrency(
                              results.breakEvenCargoValue,
                              currency,
                            )
                          : '--'}
                      </p>
                      <p className="text-muted-foreground mt-2 text-xs leading-5">
                        Cargo value where the air premium starts to pay for
                        itself on carrying cost alone.
                      </p>
                    </div>
                  </div>

                  <div className="border-border bg-card/70 rounded-2xl border p-5">
                    <div className="flex items-start gap-3">
                      <div className="bg-primary/10 mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                        <FileText className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold tracking-[0.14em] uppercase">
                          Recommendation
                        </p>
                        <p className="text-muted-foreground mt-2 text-sm leading-7">
                          {hasResults
                            ? results.recommendation
                            : 'You will see a summary recommendation here once the route and shipment size are entered.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {hasResults && results.warnings.length > 0 ? (
                    <ul className="border-warn-fg/40 bg-warn-bg text-warn-fg space-y-3 rounded-2xl border p-5 text-sm">
                      {results.warnings.map((warning) => (
                        <li key={warning} className="flex items-start gap-3">
                          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                          <span className="leading-6">{warning}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>

              {hasResults ? (
                <div className="border-primary/20 bg-card mt-8 rounded-[8px] border p-6 shadow-sm sm:p-8">
                  <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
                    <div>
                      <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase">
                        Go beyond one-off estimates
                      </p>
                      <h3 className="font-heading text-foreground mt-2 text-2xl font-bold">
                        Stop re-running this comparison in a spreadsheet
                      </h3>
                      <p className="text-muted-foreground mt-3 max-w-3xl text-sm leading-7">
                        {siteName} keeps every purchase order, shipment,
                        supplier document, and freight invoice in one workspace,
                        so the mode decision you make here shows up in the real
                        landed cost per unit instead of getting lost.
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row lg:flex-col">
                      <Button asChild className="h-11 rounded-xl font-bold">
                        <Link href={startFreePath}>
                          Start Your 14-Day Trial
                        </Link>
                      </Button>
                      <Button
                        asChild
                        variant="outline"
                        className="h-11 rounded-xl font-bold"
                      >
                        <Link href="/#features">See How It Works</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto mb-16 max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                How This Sea vs Air Freight Calculator Works
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                The freight cost estimator combines your quote rates, shipment
                density, fixed charges, and the speed premium between sea and
                air.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {estimationSteps.map((step) => {
                const Icon = step.icon;

                return (
                  <div
                    key={step.title}
                    className="border-border bg-card rounded-[8px] border p-8 shadow-sm"
                  >
                    <div className="bg-primary/10 mb-6 flex h-12 w-12 items-center justify-center rounded-xl">
                      <Icon className="text-primary h-5 w-5" />
                    </div>
                    <h3 className="text-foreground text-xl font-bold">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground mt-3 text-sm leading-7">
                      {step.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="border-border bg-card border-y py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                Sea vs Air Freight: What Actually Changes
              </h2>
              <p className="text-muted-foreground mt-4 text-base leading-7">
                Cost is only one of the four things that move when you switch
                mode. The comparison above prices all of them.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-2">
              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  How the cargo is billed
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  Sea freight bills revenue tons at 1 CBM per 1,000 kg, subject
                  to the minimum in your quote. The example starts at one W/M.
                  Air freight bills volumetric weight at{' '}
                  {FREIGHT_ASSUMPTIONS.airVolumetricKgPerCbm} kg per CBM
                  whenever it beats gross weight. Bulky, light cargo is punished
                  hardest by air. The air rule matches the{' '}
                  <a
                    href="https://www.iata.org/en/publications/newsletters/iata-knowledge-hub/air-cargo-tariffs-and-rules-what-you-need-to-know/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary font-semibold hover:underline"
                  >
                    IATA 1:6000 guidance
                  </a>
                  .
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Where the fixed costs sit
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  LCL carries origin CFS and destination handling charges that
                  do not shrink with the shipment, which is why a small LCL
                  booking can lose to air on a per-kg basis.
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  Why quote scope matters
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  Compare like with like. If one quote is port-to-port and the
                  other is door-to-door, add the missing pickup, terminal,
                  customs, and delivery charges before deciding. Use the{' '}
                  <Link
                    className="text-primary font-semibold hover:underline"
                    href="/tools/import-duty-calculator"
                  >
                    import duty calculator
                  </Link>{' '}
                  to model duties and taxes separately. For contract scope,
                  check the{' '}
                  <a
                    href="https://iccwbo.org/business-solutions/incoterms-rules/incoterms-2020/"
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary font-semibold hover:underline"
                  >
                    official ICC Incoterms rules
                  </a>
                  .
                </p>
              </div>

              <div className="border-border bg-muted rounded-2xl border p-6">
                <h3 className="text-foreground text-lg font-bold">
                  What the waiting costs
                </h3>
                <p className="text-muted-foreground mt-2 text-sm leading-7">
                  Weeks at sea tie up capital. Feed the sea or air total into
                  the{' '}
                  <Link
                    className="text-primary font-semibold hover:underline"
                    href="/tools/landed-cost-calculator"
                  >
                    landed cost calculator
                  </Link>{' '}
                  so freight sits inside cost per unit, not as a surprise
                  invoice. If one bill covers several SKUs, use{' '}
                  <Link
                    className="text-primary font-semibold hover:underline"
                    href="/blog/allocate-freight-cost-across-multiple-skus"
                  >
                    how to allocate freight across SKUs
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="border-primary/20 bg-primary/[0.06] mt-8 rounded-2xl border p-6">
              <p className="text-primary text-xs font-bold tracking-[0.18em] uppercase">
                Public Flexport examples — verify current rates
              </p>
              <h3 className="text-foreground mt-2 text-lg font-bold">
                Flexport confirms why static lane prices are misleading
              </h3>
              <p className="text-muted-foreground mt-3 text-sm leading-7">
                Flexport&apos;s public Shanghai to Los Angeles examples
                currently start at $1,149 for a 5 CBM, 750 kg LCL shipment and
                $6,226 for a separate 6 CBM, 1,000 kg air shipment. Those
                examples use different cargo dimensions, so they are useful
                benchmarks, not a valid head-to-head quote. Open the{' '}
                <a
                  href="https://www.flexport.com/rates/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  current Flexport rates page
                </a>{' '}
                and enter comparable quote totals above.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-12 lg:grid-cols-[0.9fr_1.1fr]">
              <div className="border-border relative overflow-hidden rounded-[10px] border bg-[radial-gradient(circle_at_center,var(--primary)_0%,var(--primary)_34%,var(--primary)_100%)] p-10 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.35)]">
                <div className="absolute inset-0 [background-image:linear-gradient(rgba(255,255,255,0.16)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.16)_1px,transparent_1px)] [background-size:18px_18px] opacity-20" />
                <div className="relative flex aspect-[4/3] items-center justify-center">
                  <div className="absolute h-72 w-72 rounded-full border border-white/10" />
                  <div className="absolute h-56 w-56 rounded-full border border-white/15" />
                  <div className="absolute h-40 w-40 rounded-full border border-white/20" />
                  <div className="bg-card/10 flex h-28 w-28 items-center justify-center rounded-full border border-white/20 shadow-2xl backdrop-blur-sm">
                    <Network className="text-primary-foreground h-12 w-12" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-foreground text-3xl font-bold sm:text-4xl">
                  Why Accurate Freight Estimates Matter
                </h2>

                <div className="mt-8 space-y-6">
                  {whyItMatters.map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="bg-sage-fg/10 text-sage-fg mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <h3 className="text-foreground text-lg font-bold">
                          {item.title}
                        </h3>
                        <p className="text-muted-foreground mt-1 text-sm leading-7">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <TrendingUp className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Margin planning
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Keep freight inside the landed-cost model
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-border bg-card rounded-2xl border p-5">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-xl">
                        <DollarSign className="text-primary h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-muted-foreground text-sm font-semibold">
                          Booking discipline
                        </p>
                        <p className="text-foreground text-lg font-bold">
                          Compare quotes with a baseline first
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-foreground text-center text-3xl font-bold sm:text-4xl">
              Sea vs Air Freight Calculator FAQ
            </h2>

            <Accordion type="single" collapsible className="mt-12 space-y-4">
              {faqItems.map((faq) => (
                <AccordionItem
                  key={faq.value}
                  value={faq.value}
                  className="border-border rounded-[8px] border px-6"
                >
                  <AccordionTrigger className="text-foreground text-left text-base font-bold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-6 text-sm leading-7">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-16">
              <h2 className="text-foreground text-2xl font-bold">
                Related reading
              </h2>
              <ul className="text-muted-foreground mt-4 space-y-3 text-sm leading-7">
                <li>
                  <Link
                    href="/blog/allocate-freight-cost-across-multiple-skus"
                    className="text-primary font-semibold hover:underline"
                  >
                    Allocate freight across SKUs
                  </Link>{' '}
                  — split one ocean or air bill so each product carries its
                  share.
                </li>
                <li>
                  <Link
                    href="/blog/how-to-calculate-landed-cost"
                    className="text-primary font-semibold hover:underline"
                  >
                    How to calculate landed cost
                  </Link>{' '}
                  — drop the sea or air total into cost per unit.
                </li>
                <li>
                  <Link
                    href="/tools/landed-cost-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    Landed cost calculator
                  </Link>{' '}
                  and the{' '}
                  <Link
                    href="/tools/import-duty-calculator"
                    className="text-primary font-semibold hover:underline"
                  >
                    import duty calculator
                  </Link>{' '}
                  — finish the inbound stack after you pick a mode.
                </li>
              </ul>
            </div>

            <RelatedTools currentPath="/tools/freight-cost-estimator" />
          </div>
        </section>

        <section className="bg-foreground text-background relative overflow-hidden py-24 sm:py-32">
          <div className="bg-primary/20 absolute -top-48 -right-32 h-[520px] w-[520px] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/5 blur-3xl" />

          <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
            <div className="max-w-xl">
              <p className="text-primary text-xs font-bold tracking-[0.24em] uppercase">
                The calculator is free. The follow-through is the hard part.
              </p>
              <h2 className="font-heading mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                You picked a mode. Now track what it actually cost.
              </h2>
              <p className="text-background/70 mt-6 text-lg leading-8">
                The estimate above is a decision. {siteName} is where the
                decision lives afterwards: purchase orders, supplier documents,
                shipment milestones, freight invoices, and the real landed cost
                per unit, updating as the order moves, not three months later.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  className="bg-background text-foreground hover:bg-background/90 h-12 rounded-xl px-7 font-bold"
                >
                  <Link href={startFreePath}>Start Your 14-Day Trial</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="text-background hover:bg-background/10 h-12 rounded-xl border-white/25 bg-transparent px-7 font-bold"
                >
                  <Link href="/#features">See {siteName}</Link>
                </Button>
              </div>

              <div className="text-background/60 mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {['No credit card', 'Live in 15 minutes', 'Cancel anytime'].map(
                  (item) => (
                    <span key={item} className="flex items-center gap-2">
                      <CheckCircle className="text-primary h-4 w-4" />
                      {item}
                    </span>
                  ),
                )}
              </div>
            </div>

            <figure className="bg-background text-foreground rounded-[32px] p-3 shadow-[0_32px_90px_-32px_rgba(0,0,0,0.7)] sm:p-4">
              <figcaption className="text-muted-foreground px-3 pb-3 text-xs font-bold tracking-[0.16em] uppercase">
                Illustrative {siteName} workspace
              </figcaption>
              <div className="border-border bg-card overflow-hidden rounded-[8px] border">
                <div className="border-border flex items-center justify-between border-b px-5 py-4 sm:px-7">
                  <div className="flex items-center gap-3">
                    <div className="bg-foreground text-background flex h-9 w-9 items-center justify-center rounded-xl">
                      <Network className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold">{siteName}</p>
                      <p className="text-muted-foreground text-xs">
                        Example ocean shipment
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    On track
                  </div>
                </div>

                <div className="p-5 sm:p-7">
                  <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-muted-foreground text-xs font-bold tracking-[0.18em] uppercase">
                        Shipment SA-2214 · Shanghai to Los Angeles
                      </p>
                      <p className="font-heading mt-2 text-3xl font-black sm:text-4xl">
                        $0.94
                      </p>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Actual freight cost per unit
                      </p>
                    </div>
                    <div className="rounded-full bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                      LCL · 2.5 CBM
                    </div>
                  </div>

                  <div className="mt-8 h-2 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
                    <div className="bg-primary h-full w-[68%] rounded-full" />
                  </div>
                  <div className="text-muted-foreground mt-2 flex justify-between text-xs">
                    <span>Departed origin</span>
                    <span>Arrives in 6 days</span>
                  </div>

                  <div className="mt-8 grid gap-3 sm:grid-cols-3">
                    <div className="bg-muted rounded-2xl p-4">
                      <Ship className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$440</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Ocean freight billed
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <ReceiptText className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$318</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Duty on entry
                      </p>
                    </div>
                    <div className="bg-muted rounded-2xl p-4">
                      <Package className="text-primary h-5 w-5" />
                      <p className="mt-4 text-lg font-black">$10.42</p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Landed cost per unit
                      </p>
                    </div>
                  </div>

                  <div className="border-border mt-6 flex items-center gap-3 border-t pt-5">
                    <CheckCircle className="text-primary h-5 w-5 shrink-0" />
                    <p className="text-sm font-semibold">
                      Every estimate on this page becomes a tracked number once
                      the order is real.
                    </p>
                  </div>
                </div>
              </div>
            </figure>
          </div>
        </section>
      </div>
    </>
  );
}
